import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchAdminUserById,
  isAdminRole,
  sendAdminPhoneOtp,
  uploadAdminFaceLiveness,
  verifyAdminEmail,
  verifyAdminPhoneOtp,
} from '../services/api/admin';
import {
  getAdminAuthToken,
  getAdminAuthUser,
  saveAdminAuthToken,
  saveAdminAuthTokens,
  saveAdminAuthUser,
} from '../services/storage/adminFlow';
import { resendCustomerEmailOtp } from '../services/api/customerAuth';
import { nextStageForAdmin } from '../utils/adminOnboarding';
import type { AdminUser } from '../types/admin';
import '../styles/register.css';

type ChallengeType = 'STRAIGHT' | 'LEFT' | 'RIGHT';
type CaptureState = Partial<Record<ChallengeType, { blob: Blob; previewUrl: string }>>;

const LIVENESS_STEPS: Array<{
  key: ChallengeType;
  title: string;
  hint: string;
}> = [
  {
    key: 'STRAIGHT',
    title: 'Center your face',
    hint: 'Look straight into the camera and keep your full face inside the frame.',
  },
  {
    key: 'LEFT',
    title: 'Turn slightly left',
    hint: 'Turn your head slightly left while keeping your face visible.',
  },
  {
    key: 'RIGHT',
    title: 'Turn slightly right',
    hint: 'Turn your head slightly right while keeping your face visible.',
  },
];

function normalizePhoneToE164(phone: string): string {
  const digitsOnly = phone.replace(/[^\d]/g, '');
  return digitsOnly ? `+${digitsOnly}` : '';
}

function normalizePhoneForInput(phone: string | null | undefined): string {
  return String(phone ?? '').replace(/^\+/, '');
}

const AdminOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState<AdminUser | null>(() => getAdminAuthUser());
  const [emailCode, setEmailCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [phone, setPhone] = useState(() => normalizePhoneForInput(getAdminAuthUser()?.phone));
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneMessage, setPhoneMessage] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [uploadingLiveness, setUploadingLiveness] = useState(false);
  const [currentLivenessIndex, setCurrentLivenessIndex] = useState(0);
  const [captures, setCaptures] = useState<CaptureState>({});
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stage = useMemo(() => nextStageForAdmin(adminUser), [adminUser]);
  const currentLivenessStep = LIVENESS_STEPS[currentLivenessIndex];
  const livenessComplete = LIVENESS_STEPS.every((stepConfig) => Boolean(captures[stepConfig.key]));

  useEffect(() => {
    const storedAdmin = getAdminAuthUser();
    if (!storedAdmin || !isAdminRole(storedAdmin.roles)) {
      navigate('/login', { replace: true });
      return;
    }

    setAdminUser(storedAdmin);
    setPhone(normalizePhoneForInput(storedAdmin.phone));
  }, [navigate]);

  useEffect(() => {
    if (adminUser && stage === 'completed') {
      navigate('/admin', { replace: true });
    }
  }, [adminUser, stage, navigate]);

  useEffect(() => {
    const startCamera = async () => {
      if (stage !== 'face-liveness') return;
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('This browser does not support camera access for face verification.');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: 'user',
            width: { ideal: 960 },
            height: { ideal: 720 },
          },
        });

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCameraError('');
      } catch (error: any) {
        setCameraError(error.message || 'Could not access your camera.');
      }
    };

    const stopCamera = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };

    void startCamera();
    return stopCamera;
  }, [stage]);

  useEffect(() => {
    return () => {
      Object.values(captures).forEach((capture) => {
        if (capture?.previewUrl) {
          URL.revokeObjectURL(capture.previewUrl);
        }
      });
    };
  }, [captures]);

  const persistAdminUser = async (nextUser: AdminUser, nextToken?: string) => {
    const effectiveToken = nextToken || getAdminAuthToken() || '';
    const hydratedUser =
      nextUser?.id && effectiveToken
        ? await fetchAdminUserById(nextUser.id, effectiveToken).catch(() => nextUser)
        : nextUser;

    if (nextToken) {
      saveAdminAuthToken(nextToken);
    }
    saveAdminAuthUser(hydratedUser);
    setAdminUser(hydratedUser);
  };

  const handleVerifyEmail = async () => {
    if (!adminUser) return;
    if (emailCode.trim().length !== 6) {
      setEmailError('Enter the 6-digit code from your email.');
      return;
    }

    setEmailLoading(true);
    setEmailError('');
    setEmailMessage('');

    try {
      const response = await verifyAdminEmail(adminUser.email, emailCode.trim());
      const accessToken = response.accessToken ?? response.token;
      if (accessToken && response.refreshToken) {
        saveAdminAuthTokens({
          accessToken,
          refreshToken: response.refreshToken,
          accessTokenExpiresAt: response.accessTokenExpiresAt,
          refreshTokenExpiresAt: response.refreshTokenExpiresAt,
        });
      }
      await persistAdminUser(response.user, accessToken ?? response.token);
      setEmailCode('');
      setEmailMessage('Email verified. Continue with the remaining admin setup steps.');
    } catch (error: any) {
      setEmailError(error.message || 'Email verification failed.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!adminUser) return;

    setEmailLoading(true);
    setEmailError('');
    setEmailMessage('');

    try {
      await resendCustomerEmailOtp(adminUser.email);
      setEmailMessage('A new 6-digit code has been sent to your email.');
    } catch (error: any) {
      setEmailError(error.message || 'Could not resend the email code.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSendPhoneCode = async () => {
    if (!adminUser) return;

    const phoneE164 = normalizePhoneToE164(phone);
    if (!phoneE164 || phoneE164.length < 11) {
      setPhoneError('Enter a valid phone number with country code, for example 27711234567.');
      return;
    }

    setPhoneLoading(true);
    setPhoneError('');
    setPhoneMessage('');

    try {
      await sendAdminPhoneOtp(adminUser.id, phoneE164);
      setShowPhoneModal(true);
      setPhoneCode('');
      setPhoneMessage('We sent a 6-digit code to your phone.');
    } catch (error: any) {
      setPhoneError(error.message || 'Could not send the phone verification code.');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!adminUser) return;

    const phoneE164 = normalizePhoneToE164(phone);
    if (phoneCode.trim().length !== 6) {
      setPhoneError('Enter the 6-digit code from your phone.');
      return;
    }

    setPhoneLoading(true);
    setPhoneError('');

    try {
      const updatedUser = await verifyAdminPhoneOtp(adminUser.id, phoneE164, phoneCode.trim());
      await persistAdminUser(updatedUser);
      setShowPhoneModal(false);
      setPhoneCode('');
      setPhoneMessage('Phone number verified. Continue with face verification.');
    } catch (error: any) {
      setPhoneError(error.message || 'Could not verify your phone number.');
    } finally {
      setPhoneLoading(false);
    }
  };

  const captureCurrentPose = async () => {
    if (!videoRef.current || !canvasRef.current) {
      setCameraError('Camera is not ready yet.');
      return;
    }

    setCapturing(true);
    setCameraError('');

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Could not capture the camera frame.');
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((generatedBlob) => {
          if (!generatedBlob) {
            reject(new Error('Could not create the face verification image.'));
            return;
          }

          resolve(generatedBlob);
        }, 'image/jpeg', 0.92);
      });

      setCaptures((current) => {
        const previous = current[currentLivenessStep.key];
        if (previous?.previewUrl) {
          URL.revokeObjectURL(previous.previewUrl);
        }

        return {
          ...current,
          [currentLivenessStep.key]: {
            blob,
            previewUrl: URL.createObjectURL(blob),
          },
        };
      });

      if (currentLivenessIndex < LIVENESS_STEPS.length - 1) {
        setCurrentLivenessIndex((current) => current + 1);
      }
    } catch (error: any) {
      setCameraError(error.message || 'Could not capture your face verification photo.');
    } finally {
      setCapturing(false);
    }
  };

  const uploadLivenessPack = async () => {
    if (!adminUser) return;
    if (!livenessComplete) {
      setCameraError('Capture all three face verification poses before continuing.');
      return;
    }

    setUploadingLiveness(true);
    setCameraError('');

    try {
      let latestUser = adminUser;

      for (const [index, stepConfig] of LIVENESS_STEPS.entries()) {
        const capture = captures[stepConfig.key];
        if (!capture) {
          throw new Error(`Capture ${stepConfig.title.toLowerCase()} before uploading.`);
        }

        latestUser = await uploadAdminFaceLiveness(
          adminUser.id,
          stepConfig.key,
          (index + 1) as 1 | 2 | 3,
          capture.blob,
        );
      }

      await persistAdminUser(latestUser);
    } catch (error: any) {
      setCameraError(error.message || 'Could not upload face verification images.');
    } finally {
      setUploadingLiveness(false);
    }
  };

  const renderEmailStep = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Admin Email</div>
      <h1 className="register-form-title">Verify your admin email</h1>
      <p className="register-form-copy">
        Your admin role is already provisioned internally. Before we open the admin console, confirm the email address tied to your account.
      </p>

      <div className="register-field-group">
        <div className="register-field">
          <label className="register-label" htmlFor="adminEmailAddress">
            Email Address
          </label>
          <input className="register-input" id="adminEmailAddress" readOnly type="email" value={adminUser?.email || ''} />
        </div>

        <div className="register-field">
          <label className="register-label" htmlFor="adminEmailCode">
            Verification Code
          </label>
          <input
            className="register-input"
            id="adminEmailCode"
            inputMode="numeric"
            maxLength={6}
            onChange={(event) => {
              setEmailCode(event.target.value.replace(/\D/g, '').slice(0, 6));
              setEmailError('');
            }}
            placeholder="123456"
            value={emailCode}
          />
          {emailError ? <div className="register-inline-error">{emailError}</div> : null}
          {emailMessage ? <div className="register-inline-success">{emailMessage}</div> : null}
        </div>
      </div>

      <div className="register-flow-actions">
        <button className="register-submit register-submit-inline" disabled={emailLoading} onClick={handleVerifyEmail} type="button">
          {emailLoading ? 'Verifying...' : 'Verify email'}
        </button>
        <button className="register-text-button" disabled={emailLoading} onClick={handleResendEmail} type="button">
          Resend code
        </button>
      </div>
    </div>
  );

  const renderPhoneStep = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Admin Phone</div>
      <h1 className="register-form-title">Add and verify your phone number</h1>
      <p className="register-form-copy">
        We use this number for admin account security and internal verification. Add the number you want tied to your admin identity.
      </p>

      <div className="register-field-group">
        <div className="register-field">
          <label className="register-label" htmlFor="adminPhone">
            Phone Number
          </label>
          <input
            className="register-input"
            id="adminPhone"
            inputMode="tel"
            onChange={(event) => {
              setPhone(event.target.value.replace(/[^\d]/g, ''));
              setPhoneError('');
            }}
            placeholder="e.g. 27711234567"
            type="tel"
            value={phone}
          />
          <div className="register-field-hint">Include your country code, for example 27.</div>
          {phoneError ? <div className="register-inline-error">{phoneError}</div> : null}
          {phoneMessage ? <div className="register-inline-success">{phoneMessage}</div> : null}
        </div>
      </div>

      <div className="register-flow-actions">
        <button className="register-submit register-submit-inline" disabled={phoneLoading} onClick={handleSendPhoneCode} type="button">
          {phoneLoading ? 'Sending code...' : 'Send verification code'}
        </button>
      </div>
    </div>
  );

  const renderFaceStep = () => (
    <div className="register-flow-panel">
      <div className="register-form-badge">Admin Face Check</div>
      <h1 className="register-form-title">Complete your face challenge</h1>
      <p className="register-form-copy">
        Capture the guided poses below so we can finish the live-face verification required before admin access.
      </p>

      <div className="register-liveness-progress">
        {LIVENESS_STEPS.map((stepConfig, index) => {
          const complete = Boolean(captures[stepConfig.key]);
          const active = index === currentLivenessIndex;
          return (
            <button
              className={`register-liveness-pill${active ? ' register-liveness-pill-active' : ''}${complete ? ' register-liveness-pill-complete' : ''}`}
              key={stepConfig.key}
              onClick={() => setCurrentLivenessIndex(index)}
              type="button"
            >
              {complete ? 'Done' : stepConfig.title}
            </button>
          );
        })}
      </div>

      <div className="register-camera-shell">
        {cameraError ? (
          <div className="register-camera-error">{cameraError}</div>
        ) : (
          <>
            <video autoPlay className="register-camera-video" muted playsInline ref={videoRef} />
            <div className="register-camera-overlay">
              <div className="register-camera-frame" />
              <div className="register-camera-text">
                <strong>{currentLivenessStep.title}</strong>
                <span>{currentLivenessStep.hint}</span>
              </div>
            </div>
          </>
        )}
        <canvas className="register-hidden-canvas" ref={canvasRef} />
      </div>

      <div className="register-capture-grid">
        {LIVENESS_STEPS.map((stepConfig) => {
          const capture = captures[stepConfig.key];
          return (
            <div className="register-capture-card" key={stepConfig.key}>
              <div className="register-capture-label">{stepConfig.title}</div>
              {capture ? <img alt={stepConfig.title} className="register-capture-preview" src={capture.previewUrl} /> : <div className="register-capture-placeholder">Capture pending</div>}
            </div>
          );
        })}
      </div>

      <div className="register-flow-actions">
        <button className="register-secondary-button" disabled={capturing || Boolean(cameraError)} onClick={captureCurrentPose} type="button">
          {capturing ? 'Capturing...' : `Capture ${currentLivenessStep.title}`}
        </button>
        <button className="register-submit register-submit-inline" disabled={!livenessComplete || uploadingLiveness} onClick={uploadLivenessPack} type="button">
          {uploadingLiveness ? 'Uploading...' : 'Finish admin setup'}
        </button>
      </div>
    </div>
  );

  return (
    <section className="register-page">
      <div className="register-shell register-shell-single">
        <div className="register-panel register-panel-form">
          <div className="register-form-header">
            <div className="register-form-heading-row">
              <div className="register-form-badge">Admin Setup</div>
              <Link className="register-home-link" to="/">
                &larr; Home
              </Link>
            </div>
            <h1 className="register-form-title">Complete admin access setup</h1>
            <p className="register-form-copy">
              Your admin role comes from an internal process. We still need to complete identity and contact checks before opening the admin console.
            </p>
          </div>

          {stage === 'verify-email' ? renderEmailStep() : null}
          {stage === 'phone-verification' ? renderPhoneStep() : null}
          {stage === 'face-liveness' ? renderFaceStep() : null}
        </div>
      </div>

      {showPhoneModal ? (
        <div className="register-modal-backdrop" role="presentation">
          <div className="register-modal" role="dialog" aria-modal="true" aria-labelledby="admin-phone-modal-title">
            <button
              aria-label="Close phone verification"
              className="register-modal-close"
              onClick={() => setShowPhoneModal(false)}
              type="button"
            >
              ×
            </button>
            <div className="register-status-badge">Verify Phone</div>
            <h2 className="register-modal-title" id="admin-phone-modal-title">
              Enter the code from your phone
            </h2>
            <p className="register-modal-copy">We sent a 6-digit code to {phone}.</p>
            <input
              className="register-input register-modal-input"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => {
                setPhoneCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                setPhoneError('');
              }}
              placeholder="123456"
              value={phoneCode}
            />
            {phoneError ? <div className="register-inline-error">{phoneError}</div> : null}
            <div className="register-modal-actions">
              <button className="register-submit register-submit-inline" disabled={phoneLoading} onClick={handleVerifyPhone} type="button">
                {phoneLoading ? 'Verifying...' : 'Verify phone'}
              </button>
              <button className="register-text-button" disabled={phoneLoading} onClick={handleSendPhoneCode} type="button">
                Resend code
              </button>
              <button className="register-text-button" disabled={phoneLoading} onClick={() => setShowPhoneModal(false)} type="button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default AdminOnboardingPage;
